/**
 * TagManager - A comprehensive tag management component that allows users to create, edit, and delete tags
 * with color selection in a drawer interface.
 */

import React, { useEffect, useRef } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import {
  Drawer,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';

import useTagsCompatibilityDisplay from 'hooks/useTagsCompatibilityDisplay';
import useTagsData from 'hooks/useTagsData';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';
import { useToast } from 'components/ToastContainer';
import PlusIcon from 'assets/plus.svg?react';
import PlusCurrentColorIcon from 'assets/plus-current-color.svg?react';
import NoTagsIcon from 'assets/no_tags_found.svg';
import {
  TagFormValues,
  tagManagerValidationSchema,
  useTagManagerInitialValues,
  validateTagNameWithDuplicates
} from './schema';
import {
  createHandleSave,
  createNewTagData,
  shouldEnableSave,
  processDisplayTags
} from './helpers';
import TagColorRow from './components/TagColorRow';
import {
  TAG_ACTION_STRINGS,
  TAG_MANAGER_STRINGS,
  TAG_STRINGS
} from './constants';
import './index.css';

interface TagManagerProps {
  open: boolean;
  onClose: () => void;
  onTagsUpdated?: () => void;
}

const TagManager: React.FC<TagManagerProps> = ({
  open,
  onClose,
  onTagsUpdated
}) => {
  // Track if we've already auto-created a tag in this session
  const hasAutoCreatedRef = useRef(false);

  // Always reset auto-create state when drawer is closed, even if close happens externally.
  useEffect(() => {
    if (!open) {
      hasAutoCreatedRef.current = false;
    }
  }, [open]);

  const { tags, loading, saving, saveTags, getTags } = useTagsData() as {
    tags: Array<{
      id: string;
      name: string;
      color: string;
      description?: string;
    }>;
    loading: boolean;
    saving: boolean;
    saveTags: (
      formValues: any,
      enhancedTags: any
    ) => Promise<{
      success: boolean;
      partialSuccess?: boolean;
      error?: any;
    }>;
    getTags: () => void;
  };

  const toast = useToast();

  const enhancedTags = useTagsCompatibilityDisplay(tags || []);

  const initialValues = useTagManagerInitialValues(enhancedTags);

  const handleSave = createHandleSave(
    saveTags,
    toast,
    enhancedTags,
    onTagsUpdated,
    onClose,
    getTags
  );

  const renderDrawerContent = (
    setFieldValue: FormikHelpers<TagFormValues>['setFieldValue'],
    values: TagFormValues,
    resetForm: FormikHelpers<TagFormValues>['resetForm'],
    validateForm: FormikProps<TagFormValues>['validateForm'],
    handleCreateNewTag: () => void,
    handleSubmit: FormikProps<TagFormValues>['handleSubmit']
  ) => {
    if (loading || saving) {
      return (
        <LoadingSpinner
          className="tag-manager-spinner"
          message={
            saving
              ? TAG_MANAGER_STRINGS.SAVING_TAGS
              : TAG_MANAGER_STRINGS.LOADING_TAGS
          }
        />
      );
    }

    // Use displayTags length since it represents the actual rendered tags
    const displayTags = processDisplayTags(enhancedTags, values);
    const totalTagsCount = displayTags.length;

    // Auto-create a tag only on first open when there are no tags AND form hasn't been modified
    // Don't auto-create if user has actively deleted tags
    const isInitialEmptyState =
      open &&
      totalTagsCount === 0 &&
      !hasAutoCreatedRef.current &&
      (!values?.tags || Object.keys(values.tags).length === 0) &&
      enhancedTags.length === 0;

    if (isInitialEmptyState) {
      hasAutoCreatedRef.current = true;
      handleCreateNewTag();
    }

    // Show empty state if no tags and we've already auto-created or user deleted tags
    if (totalTagsCount === 0) {
      return (
        <EmptyTableState
          title={TAG_STRINGS.emptyStateTitle}
          message=" "
          overlayIcon={NoTagsIcon}
          containerClassName="w-full h-full pt-34px pb-66px flex flex-col justify-start items-center"
          button={
            <Button
              type={ButtonType.primary}
              size="large"
              icon={<PlusIcon />}
              onClick={handleCreateNewTag}
            >
              {TAG_ACTION_STRINGS.createNewTag}
            </Button>
          }
        />
      );
    }

    if (
      !setFieldValue ||
      !values ||
      !resetForm ||
      !validateForm ||
      !handleSubmit
    ) {
      return null;
    }

    const handleColorChange = (tagId: string, color: string) => {
      setFieldValue(`tags.${tagId}.color`, color);
    };

    const handleDeleteTag = (tagId: string) => {
      const updatedTags = { ...values.tags };
      delete updatedTags[tagId];
      setFieldValue(TAG_MANAGER_STRINGS.TAGS_FIELD, updatedTags);
    };

    return (
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="tag-manager-content" style={{ paddingBottom: '24px' }}>
          <div>
            <div style={{ padding: '2px 0px' }}>
              {displayTags.map(tag => {
                const tagData = values.tags[tag.id];
                const isNewTag = tagData?.source === 'create';
                const originalName =
                  enhancedTags.find(t => t.id === tag.id)?.name ?? '';

                return (
                  <TagColorRow
                    key={tag.id}
                    tag={tag}
                    onColorChange={handleColorChange}
                    onDelete={handleDeleteTag}
                    onCreateNewTag={handleCreateNewTag}
                    totalTagsCount={displayTags.length}
                    formValues={values}
                    isNewTag={isNewTag}
                    originalName={originalName}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </form>
    );
  };

  const renderDrawerFooter = (
    handleDrawerClose: () => void,
    handleCreateNewTag?: () => void,
    handleSaveClick?: () => void,
    finalSaveState?: boolean,
    isLoading?: boolean,
    isSaving?: boolean
  ) => {
    return (
      <div className="flex justify-between items-center w-full">
        <Button
          type={ButtonType.outline}
          onClick={handleDrawerClose}
          disabled={isSaving}
          size="large"
          style={{ fontSize: 14 }}
        >
          {TAG_MANAGER_STRINGS.CANCEL}
        </Button>
        <div className="flex gap-2">
          <Button
            type={ButtonType.primary}
            onClick={handleCreateNewTag}
            disabled={isLoading || isSaving}
            icon={<PlusCurrentColorIcon />}
            ghost
            size="large"
            style={{ fontSize: 14 }}
          >
            {TAG_MANAGER_STRINGS.CREATE_NEW_TAG}
          </Button>
          <Button
            type={ButtonType.primary}
            onClick={() => {
              if (handleSaveClick) {
                handleSaveClick();
              }
            }}
            disabled={isLoading || isSaving || !finalSaveState}
            size="large"
            style={{ fontSize: 14 }}
          >
            {isSaving ? TAG_MANAGER_STRINGS.SAVING : TAG_MANAGER_STRINGS.SAVE}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={tagManagerValidationSchema}
      validateOnChange
      validateOnBlur
      enableReinitialize
      onSubmit={async values => {
        await handleSave(values);
      }}
    >
      {({ setFieldValue, values, resetForm, validateForm, handleSubmit }) => {
        const handleDrawerClose = () => {
          resetForm();
          onClose();
        };

        const finalSaveState = shouldEnableSave(
          loading,
          saving,
          values,
          enhancedTags,
          validateTagNameWithDuplicates
        );

        const handleCreateNewTag = () => {
          const newTag = createNewTagData();

          setFieldValue(`tags.${newTag.id}`, newTag.data);

          setTimeout(() => {
            validateForm();
          }, 0);
        };

        return (
          <Drawer
            title={
              <span className="text-xl font-semibold leading-7">
                {TAG_MANAGER_STRINGS.DRAWER_TITLE}
              </span>
            }
            width={480}
            open={open}
            onClose={handleDrawerClose}
            bodyStyle={{
              paddingLeft: '24px',
              paddingRight: '24px',
              paddingTop: 0,
              paddingBottom: 0
            }}
            destroyOnClose
            closable={false}
            className="tag-manager-drawer"
            footer={renderDrawerFooter(
              handleDrawerClose,
              handleCreateNewTag,
              handleSubmit,
              finalSaveState,
              loading,
              saving
            )}
          >
            {renderDrawerContent(
              setFieldValue,
              values,
              resetForm,
              validateForm,
              handleCreateNewTag,
              handleSubmit
            )}
          </Drawer>
        );
      }}
    </Formik>
  );
};

export default TagManager;
